import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "src/components/Toast";
import { translateErrorMessage, shouldTranslateError } from "src/utils/error";

export interface SafeApiCallOptionsTypes {
  toastSuccessMsg?: string;
  toastErrorMsg?: string;
  useServerErrorAsToast?: boolean;
  retryFn?: () => void;
  suppressErrorRedirect?: boolean;
  suppressDuplicateToasts?: boolean;
}

export const useSafeApiCall = () => {
  const lastErrorToastKey = useRef("");
  const { setPendingToast } = useToast();
  const navigate = useNavigate();

  return function safeApiCall<T>(
    apiCall: () => Promise<T>,
    options?: SafeApiCallOptionsTypes | null,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const {
        toastSuccessMsg,
        toastErrorMsg,
        retryFn,
        suppressErrorRedirect,
        suppressDuplicateToasts,
        useServerErrorAsToast,
      } = options || {};

      apiCall()
        .then((data) => {
          if (toastSuccessMsg) {
            setPendingToast(toastSuccessMsg, "success");
          }
          resolve(data);
        })
        .catch((err: any) => {
          const errorKey = `${toastErrorMsg ?? ""}_${err?.status}`;
          if (
            !suppressDuplicateToasts ||
            errorKey !== lastErrorToastKey.current ||
            retryFn
          ) {
            lastErrorToastKey.current = errorKey;

            let errorMessage = toastErrorMsg;

            // For 4xx errors, try to translate the server error message
            if (shouldTranslateError(err?.status) && err?.message) {
              const translatedMessage = translateErrorMessage(err.message);
              errorMessage = translatedMessage;
            }

            if (errorMessage) {
              setPendingToast(errorMessage, "error", retryFn);
            } else if (useServerErrorAsToast && err?.message) {
              // Fallback to raw server message if no custom message provided
              const message = shouldTranslateError(err?.status)
                ? translateErrorMessage(err.message)
                : err.message;
              setPendingToast(message, "error", retryFn);
            }
          }

          if (
            !suppressErrorRedirect &&
            (err?.status === 403 || err?.status === 404)
          ) {
            navigate("/error", {
              state: { status: err.status },
              replace: true,
            });
          }

          reject(err);
        });
    });
  };
};
